import { DataSource } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { map } from 'rxjs/operators';
import {Observable, of as observableOf, merge, BehaviorSubject} from 'rxjs';
import {Certificate, RiskFactor} from '../../graphql/graphql';

/**
 * Data source for the CertificatesTable view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class RiskFactorsTableDatasource extends DataSource<RiskFactor> {
  data = new BehaviorSubject<RiskFactor[]>([]);
  filter = new BehaviorSubject<string>('');
  sort: MatSort;
  filteredData;

  constructor() {
    super();
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<RiskFactor[]> {
    // Combine everything that affects the rendered data into one update
    // stream for the data-table to consume.
    const dataMutations = [
      this.data.asObservable(),
      this.sort.sortChange,
      this.filter.asObservable()
    ];

    return merge(...dataMutations).pipe(map(v => {
      return this.getSortedData([...this.data.value], this.filter.value);
    }));
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect() {}

  /**
   * Paginate the data (client-side). If you're using server-side pagination,
   * this would be replaced by requesting the appropriate data from the server.
   */


  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: RiskFactor[], filter?: string) {
    let filteredData;
    if (this.filter) {
      filteredData = data.filter(e => e.title.toLowerCase().includes(filter.toLowerCase()));
    } else {
      filteredData = data;
    }
    if (!this.sort.active || this.sort.direction === '') {
      return filteredData;
    }

    return filteredData.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        case 'name': return compare(a.title, b.title, isAsc);
        case 'id': return compare(+a.id, +b.id, isAsc);
        default: return 0;
      }
    });
  }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a, b, isAsc) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
